import React, { useState, useEffect } from 'react';
import { setProjectName } from 'store/actions';
import { useDispatch } from 'react-redux';
import { Space } from 'antd';
import {
  Card,
  Button,
  Table,
  Typography,
  Tag,
  Row,
  Col,
  Input,
  Select,
  Form,
  Modal,
  DatePicker,
  Tooltip,
} from 'antd';
import { useParams } from 'react-router-dom';
import httpInjectorService from 'services/http-injector.service';
import { toast } from 'react-toastify';
import { create, get } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Menu } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import Cookies from 'js-cookie';
import { decryptData } from 'utils/crypto';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const TaskManagement = () => {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [createProjectModalVisible, setCreateProjectModalVisible] =
    useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const dispatch = useDispatch();

  const [selectedRole, setSelectedRole] = useState(null);
  const [roleAssignments, setRoleAssignments] = useState({});
  const [searchText, setSearchText] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [editProjectModal, setIsEditProjectModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  //const [roleId, setRoleId] = useState(Cookies.get('role_id'));
  const roleId = decryptData(Cookies.get('role_id'));
  const userId = decryptData(Cookies.get('user_id'));
  // Filter projects based on search text

  const navigate = useNavigate();

  useEffect(() => {
    getProjects();
    getUserPermissions();
    getUsersForDepartment();
  }, []);

  useEffect(() => {
    const projectName = form.getFieldValue('project_name');
    if (projectName) {
      const autoGeneratedKey = projectName.toUpperCase().replace(/\s+/g, '-'); // Replace spaces with hyphens
      form.setFieldsValue({ project_key: autoGeneratedKey });
    }
  }, [form.getFieldValue('project_name')]);

  const getUsersForDepartment = async () => {
    try {
      const response = await httpInjectorService.getUsersByDepartment();

      if (response.status === 'success') {
        setUsers(response.data);
      } else {
        console.error('Failed to fetch users:', response.message);
      }
    } catch (err) {
      console.error('Error while fetching users:', err);
      toast.error('Failed to fetch users');
    }
  };

  const getUserPermissions = async () => {
    try {
      const response =
        await httpInjectorService.getALLProjectRolePermissions(userId);
      if (response.status === 'success') {
        setUserPermissions(response.data);
      }
    } catch (err) { }
  };

  const getProjects = async () => {
    try {
      const response = await httpInjectorService.getProjects();
      if (response.status === 'success') {
        const formattedProjects = response.data.map((project) => {
          const date = new Date(project.created_at);
          const formattedDate = `${String(date.getDate()).padStart(
            2,
            '0',
          )}/${String(date.getMonth() + 1).padStart(
            2,
            '0',
          )}/${date.getFullYear()}`;
          return {
            ...project,
            created_at: formattedDate,
          };
        });
        setProjects(formattedProjects);
      } else {
        console.error('Failed to fetch projects:', response.message);
      }
    } catch (err) {
      console.error('Error while fetching projects:', err);
    }
  };

  useEffect(() => {
    if (selectedProject) {
      // console.log(selectedProject, 'selectedproject');
      editForm.setFieldsValue({
        projectName: selectedProject.project_name || '',
        projectKey: selectedProject.project_key || '',
        productionUrl: selectedProject.production_url || '',
        developmentUrl: selectedProject.development_url || '',
        repository: selectedProject.git_repo_url || '',
      });
    }
  }, [selectedProject, editForm]);

  const handleCreateProject = async () => {
    try {
      const values = await form.validateFields();

      const roleToIdMap = {
        Administrator: 1,
        Member: 3,
        Viewer: 5,
      };

      const enrichedTeamMembers = [];

      Object.entries(roleAssignments).forEach(([role, userObjects]) => {
        userObjects.forEach(({ value: userId }) => {
          const matchedUser = users.find((u) => u.user_id === userId);
          if (matchedUser) {
            enrichedTeamMembers.push({
              user_id: matchedUser.user_id,
              username: matchedUser.username,
              project_role_id: roleToIdMap[role],
              project_role: role,
            });
          }
        });
      });

      const formattedValues = {
        project_name: values.project_name,
        project_key: values.project_key,
        production_url: values.production_url,
        development_url: values.development_url,
        description: values.description,
        git_repo_url: values.repository,
        access: values.access,
        // start_date: values.start_date.format('YYYY/MM/DD'),
        end_date: '', // Optional
        team_members: enrichedTeamMembers,
      };

      const response = await httpInjectorService.addProject(formattedValues);
      if (response.status === 'success') {
        toast.success(response.message, {
          position: 'top-right',
          autoClose: 1000,
        });
        setCreateProjectModalVisible(false);
        setRoleAssignments({});
        form.resetFields();
        getUserPermissions();
        getProjects(); // Refresh
      } else {
        toast.error(response.message, {
          position: 'top-right',
          autoClose: 1000,
        });
      }
    } catch (error) {
      console.error('Validation failed:', error);
      const { errorFields } = error;
      if (errorFields.length > 0) {
        toast.error('Please fill in all required fields.', {
          position: 'top-right',
          autoClose: 2000,
        });
      }
    }
  };

  const filteredProjects = projects.filter((project) =>
    Object.values(project).some((value) =>
      String(value).toLowerCase().includes(searchText.toLowerCase()),
    ),
  );

  // Function to filter users based on role assignments
  const getAvailableUsersForRole = (role) => {
    // Flatten all assigned user IDs from roleAssignments
    const assignedUserIds = Object.values(roleAssignments)
      .flat()
      .map((user) => user.value); // Extract user_id from labelInValue format

    // Return users not already assigned to any role
    return users.filter((user) => !assignedUserIds.includes(user.user_id));
  };

  const columns = [
    {
      title: '🧩 Project Name',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 180,
      sorter: (a, b) => a.project_name.localeCompare(b.project_name),
      render: (text, record, index) => (
        <strong
          style={{ color: '#1677ff', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            dispatch(setProjectName(record.project_name));
            Cookies.set("project_name", record.project_name)
            handleRowClick(record, index);
            setSelectedProject(record);
          }}
        >
          {text}
        </strong>
      ),
    },
    {
      title: '🔑 Key',
      dataIndex: 'project_key',
      key: 'project_key',
      width: 120,
      sorter: (a, b) => a.project_key.localeCompare(b.project_key),
    },
    {
      title: '📘 Description',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      sorter: (a, b) => a.description.localeCompare(b.description),
    },
    {
      title: '📌 Status',
      dataIndex: 'project_status',
      key: 'project_status',
      width: 120,
      render: (status) => (
        <Tag color={status === 'Active' ? 'green' : 'volcano'}>{status}</Tag>
      ),
      sorter: (a, b) => a.project_status.localeCompare(b.project_status),
    },
    {
      title: '🌐 Production URL',
      dataIndex: 'production_url',
      key: 'production_url',
      width: 180,
      render: (url) => {
        if (!url) return <span style={{ color: '#999' }}>Not available</span>;
        const normalizedUrl =
          url.startsWith('http://') || url.startsWith('https://')
            ? url
            : `https://${url}`;
        return (
          <a
            href={normalizedUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={normalizedUrl}
          >
            Visit
          </a>
        );
      },
    },
    {
      title: '🔧 Development URL',
      dataIndex: 'development_url',
      key: 'development_url',
      width: 180,
      render: (url) => {
        if (!url) return <span style={{ color: '#999' }}>Not available</span>;
        const normalizedUrl =
          url.startsWith('http://') || url.startsWith('https://')
            ? url
            : `https://${url}`;
        return (
          <a
            href={normalizedUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={normalizedUrl}
          >
            Visit
          </a>
        );
      },
    },
    {
      title: '⏰ Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (text) => <span>{text}</span>,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: '👤 Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      width: 160,
      render: (createdBy) => <span>{createdBy?.username || 'Unknown'}</span>,
      sorter: (a, b) =>
        (a.created_by?.username || '').localeCompare(
          b.created_by?.username || '',
        ),
    },
    {
      title: '⚙️ Actions',
      key: 'actions',
      fixed: 'right',
      width: 160,
      render: (_, record) => {
        const hasAdminAccess = userPermissions.some(
          (perm) => perm.project_id === record.project_id && perm.role_id === 1,
        );

        if (!hasAdminAccess)
          return (
            <Tooltip title="You don't have permission to perform actions on this project.">
              <span style={{ color: '#999' }}>Not Allowed</span>
            </Tooltip>
          );

        const menu = (
          <Menu
            onClick={({ key }) => {
              if (key === 'edit') {
                setIsEditProjectModal(true);
                setSelectedProject(record);
                handleEditProject(record);
              } else if (key === 'delete') {
                setIsDeleteModalVisible(true);
                setProjectToDelete(record.project_id);
              }
            }}
          >
            <Menu.Item key="edit" style={{ color: '#1890ff' }}>
              Edit Project
            </Menu.Item>
            <Menu.Item key="delete" danger>
              Delete Project
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={['click']} data-tour="project-actions">
            <Button type="link" onClick={(e) => e.stopPropagation()}>
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const handleDemoProject=async()=>{
    try{
      const response=await httpInjectorService.createDemoProject();
      if(response.status==='success'){
        toast.success(response.message, {
          position: 'top-right',
          autoClose: 3000,
        });
        getProjects();
      }
    } catch (err) {
      toast.error(err, {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }

  const handleEditProject = async (record) => {
    //console.log(record, 'records');
    try {
      const values = await editForm.validateFields();
      const reqBody = {
        project_id: selectedProject?.project_id,
        project_name: values.projectName.trim(),
        project_key: values.projectKey,
        git_repo_url: values.respository,
        production_url: values.productionUrl,
        development_url: values.developmentUrl,
      };
      //console.log(values, 'project vals');
      const response =
        await httpInjectorService.taskManagementEditProject(reqBody);
      if (response.status === 'success') {
        toast.success(response.message, {
          position: 'top-right',
          autoClose: 3000,
        });
        setIsEditProjectModal(false);
        setSelectedProject(null);
        editForm.resetFields();
        getUserPermissions();
        getProjects();
      } else {
        toast.error(response.message, {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (err) {
      toast.error(err, {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const handleDeleteProject = async () => {
    //  const projectNumber = Number(project_id.id);
    try {
      const response =
        await httpInjectorService.taskManagementDeleteProject(projectToDelete);
      if (response.status === 'success') {
        toast.success(response.message, {
          position: 'top-right',
          autoClose: 3000,
        });
        setIsDeleteModalVisible(false);
        setProjectToDelete(null);
        getProjects();
      } else {
        toast.error(response.message, {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (err) {
      toast.error(err, {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const handleRowClick = async (record) => {
    // const project_id: Number(project_id.id)
    const id = Number(record.project_id);
    // console.log(id,"project iddddd logggg")
    try {
      const response = await httpInjectorService.getSingleProjectPermission(id);
      //Cookies.set('task-role-id', String(response.data.role_id));
      //setRoleId(response.data.role_id);
      if (response.status === 'success') {
        navigate(`/admin/taskBoard/${record.project_id}`);
      } else {
        toast.error('You do not have permission to open this project', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (err) { }
  };

  // const handleRowClick = (record, index) => {
  //   console.log('Clicked Row Data:', record);
  //   navigate(`/admin/taskBoard/${record.project_id}`);
  //   // navigate(`/taskmanagement/${record.project_id}`);
  //   //getSelectedProjectRoles();
  // };

  const confirmCloseModal = () => {
    Modal.confirm({
      title: 'Unsaved Changes',
      content: 'Unsaved data will be lost. Do you want to continue?',
      onOk() {
        setCreateProjectModalVisible(false);
        resetForm();
      },
      onCancel() {
        // Do nothing on cancel
      },
      okButtonProps: {
        style: {
          backgroundColor: '#722ed1', // Ant Design purple
          borderColor: '#722ed1',
          color: '#fff',
        },
      },
    });
  };

  const resetForm = () => {
    form.resetFields();
    setUsers([]);
    setRoleAssignments({});
    setSelectedRole(null);
  };

  return (
    <React.Fragment>
      <Card style={{ marginTop: '50px' }}>
        {/* Header Row */}
        <Row justify="space-between" align="middle" >
          <Col data-tour="project-header">
            <Title level={3} style={{ margin: 0 }}>
              📋 Project Directory
            </Title>
          </Col>
          <Col>
            {(roleId === 1 || roleId === 2) && (
              <Space>
                <Button
                  type="primary"
                  style={{ backgroundColor: '#722ed1' }}
                  onClick={handleDemoProject}
                  data-tour="try-demo-btn"
                >
                  Try Demo
                </Button>

                <Button
                  type="primary"
                  style={{ backgroundColor: '#722ed1' }}
                  onClick={() => {
                    setCreateProjectModalVisible(true);
                  }}
                  data-tour="create-project-btn"
                >
                  Create Project
                </Button>
              </Space>
            )}
          </Col>
        </Row>

        {/* Search and Row Count Filter */}
        <Row style={{ marginTop: 20 }}>
          <Col span={24}>
            <div style={{ display: 'flex', gap: '16px' }} data-tour="project-search">
              <Search
                placeholder="Search projects"
                allowClear
                onSearch={(value) => setSearchText(value)}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: '300px' }}
              />
              <Select
                defaultValue={10}
                style={{ width: 120 }}
                onChange={(value) => setPageSize(value)}
              >
                <Option value={10}>10 rows</Option>
                <Option value={20}>20 rows</Option>
                <Option value={30}>30 rows</Option>
              </Select>
            </div>
          </Col>
        </Row>

        {/* Table */}
        <div style={{ position: 'relative', zIndex: 0 }} data-tour="project-table" >
          <Table
            columns={columns}
            dataSource={filteredProjects}
            rowKey="project_id"
            bordered
            pagination={{ pageSize }}
            size="middle"
            scroll={{ x: 1300 }}
            style={{ marginTop: '20px', cursor: 'pointer' }}
          />
        </div>
      </Card>

      <Modal
        title="Create Project"
        visible={createProjectModalVisible}
        onCancel={confirmCloseModal}
        footer={null}
        width={800}
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Project Name"
                name="project_name"
                rules={[
                  { required: true, message: 'Please enter project name' },
                  {
                    min: 4,
                    message: 'Project name must be at least 4 characters long',
                  },
                  {
                    validator: (_, value) => {
                      if (!value || /[a-zA-Z0-9]/.test(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          'Project name must include at least one letter or number',
                        ),
                      );
                    },
                  },
                ]}
              >
                <Input
                  onChange={(e) => {
                    const projectName = e.target.value;
                    const autoGeneratedKey = projectName
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, '');
                    form.setFieldsValue({ project_key: autoGeneratedKey });
                  }}
                  placeholder="e.g. Samlite CRM"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Project Key"
                name="project_key"
                rules={[
                  { required: true, message: 'Please enter project key' },
                  {
                    pattern: /^[A-Z0-9]+$/,
                    message:
                      'Project key must contain only uppercase letters and numbers',
                  },
                ]}
              >
                <Input placeholder="e.g. SAMLITECRM" />
              </Form.Item>
            </Col>
          </Row>

          {/* <Col span={12}> */}
          {/* <Form.Item
                label="Access Type"
                name="access"
                rules={[
                  { required: true, message: 'Please select access type' },
                ]}
              >
                <Select
                  placeholder="Select access type"
                  optionLabelProp="label"
                >
                  <Select.Option value="private" label="Private">
                    <div>
                      <strong>Private</strong>
                      <div
                        style={{
                          whiteSpace: 'normal',
                          fontSize: '12px',
                          color: '#888',
                        }}
                      >
                        Only admins and people you add to the project can search
                        for, view, create, or edit its issues.
                      </div>
                    </div>
                  </Select.Option>

                  <Select.Option value="open" label="Open">
                    <div>
                      <strong>Open</strong>
                      <div
                        style={{
                          whiteSpace: 'normal',
                          fontSize: '12px',
                          color: '#888',
                        }}
                      >
                        Anyone with access to the "nubaxdatalabs" site can
                        search for and view this project's issues.
                      </div>
                    </div>
                  </Select.Option>
                </Select>
              </Form.Item> */}
          <Form.Item
            label="Access Type"
            name="access"
            initialValue="private"
            rules={[{ required: true, message: 'Please select access type' }]}
          >
            <Tooltip
              title="Only admins and people you add to the project can search for, view, create, or edit its issues."
              placement="right"
            >
              <Input value="Private" disabled />
            </Tooltip>
          </Form.Item>
          {/* </Col> */}

          <Form.Item
            label="Description"
            name="description"
            rules={[
              { required: true, message: 'Please enter project description' },
            ]}
          >
            <Input.TextArea placeholder="Describe your project" />
          </Form.Item>
          <Form.Item label="Github Repository" name="repository">
            <Input placeholder="e.g. https://github.com/owner/repository-name" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Production URL" name="production_url">
                <Input placeholder="e.g. https://www.example.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Development URL" name="development_url">
                <Input placeholder="e.g. https://dev.example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Select Administrators"
            name="administrators"
            rules={[
              {
                required: true,
                message: 'Please assign at least one Administrator',
              },
            ]}
          >
            <Select
              mode="multiple"
              labelInValue
              showSearch
              optionFilterProp="label"
              placeholder="Select administrators"
              value={roleAssignments['Administrator'] || []}
              onChange={(selectedUsers) => {
                setRoleAssignments((prev) => ({
                  ...prev,
                  Administrator: selectedUsers,
                }));
                form.setFieldsValue({ administrators: selectedUsers });
              }}
              options={getAvailableUsersForRole('Administrator').map(
                (user) => ({
                  label: user.username,
                  value: user.user_id,
                }),
              )}
            />
          </Form.Item>

          <Form.Item label="Select Members" name="members">
            <Select
              mode="multiple"
              labelInValue
              showSearch
              optionFilterProp="label"
              placeholder="Select members"
              value={roleAssignments['Member'] || []}
              onChange={(selectedUsers) => {
                setRoleAssignments((prev) => ({
                  ...prev,
                  Member: selectedUsers,
                }));
                form.setFieldsValue({ members: selectedUsers });
              }}
              options={getAvailableUsersForRole('Member').map((user) => ({
                label: user.username,
                value: user.user_id,
              }))}
            />

          </Form.Item>

          <Form.Item label="Select Viewers" name="viewers">
            <Select
              mode="multiple"
              labelInValue
              showSearch
              optionFilterProp="label"
              placeholder="Select viewers"
              value={roleAssignments['Viewer'] || []}
              onChange={(selectedUsers) => {
                setRoleAssignments((prev) => ({
                  ...prev,
                  Viewer: selectedUsers,
                }));
                form.setFieldsValue({ viewers: selectedUsers });
              }}
              options={getAvailableUsersForRole('Viewer').map((user) => ({
                label: user.username,
                value: user.user_id,
              }))}
            />

          </Form.Item>

          <Button
            type="primary"
            style={{ backgroundColor: '#722ed1' }}
            onClick={handleCreateProject}
          >
            Create
          </Button>
        </Form>
      </Modal>

      <Modal
        title="Update Project Details"
        visible={editProjectModal}
        onOk={handleEditProject}
        onCancel={() => {
          editForm.resetFields();
          setSelectedProject(null);
          setIsEditProjectModal(false);
        }}
        okText="Save"
        cancelText="Cancel"
        okButtonProps={{
          style: {
            backgroundColor: '#722ED1',
            borderColor: '#722ED1',
            color: '#fff',
          },
        }}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            label="Project Name"
            name="projectName"
            rules={[
              { required: true, message: 'Please enter the project name' },
            ]}
          >
            <Input placeholder="e.g. Task Management System" disabled />
          </Form.Item>

          <Form.Item
            label="Project Key"
            name="projectKey"
            rules={[
              { required: true, message: 'Please enter the project key' },
              {
                pattern: /^[A-Z0-9]+$/,
                message:
                  'Only uppercase letters and numbers are allowed. No spaces, hyphens, or special characters.',
              },
            ]}
          >
            <Input placeholder="e.g. TMS001" disabled />
          </Form.Item>
          <Form.Item label="Production URL" name="productionUrl">
            <Input placeholder="https://prod.example.com" />
          </Form.Item>

          <Form.Item label="Development URL" name="developmentUrl">
            <Input placeholder="https://dev.example.com" />
          </Form.Item>
          <Form.Item label="Github Repository" name="repository">
            <Input placeholder="e.g. https://github.com/owner/repository-name" />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Confirm Deletion"
        visible={isDeleteModalVisible}
        onOk={handleDeleteProject}
        onCancel={() => {
          setIsDeleteModalVisible(false);
          setProjectToDelete(null);
        }}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{
          style: {
            backgroundColor: '#FF0033',
            borderColor: '#d8002bff',
            color: '#fff',
          },
        }}
      >
        <p>
          Are you sure you want to <strong>permanently delete</strong> this
          project? This action cannot be undone and all associated data will be
          lost.
        </p>
      </Modal>
    </React.Fragment>
  );
};

export default TaskManagement;
